import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Full-page cart, styled to the Shoob v2 Figma "Cart" screen: line items on the
// left (thumbnail, deliverable summary, change-photo, remove), an order summary
// on the right (coupon, free-shipping progress, totals) and a checkout CTA.
// All data comes from the gated cart/coupon/cart_items JSON endpoints; "Checkout"
// hands off to the existing Stripe checkout page.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const itemSummary = (item) => {
  const parts = [];
  if (item.print_count > 0) parts.push(`${item.print_count} print${item.print_count === 1 ? '' : 's'}`);
  if (item.digital_count > 0) parts.push(item.print_count > 0 ? 'digital' : 'Digital download');
  return parts.join(' + ');
};

const s = {
  page: { background: t.pageBg, minHeight: '100vh', paddingBottom: '2rem' },
  inner: { maxWidth: 1080, margin: '0 auto', padding: '1.5rem 1.25rem' },
  back: {
    background: 'none',
    border: 'none',
    color: t.brand,
    cursor: 'pointer',
    padding: 0,
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1.5rem', alignItems: 'start',
  },
  card: {
    background: t.surface, borderRadius: t.radius, boxShadow: t.shadowSm, border: '1px solid rgba(0,0,0,.06)',
  },
  cardHead: {
    padding: '1rem 1.25rem', borderBottom: `1px solid ${t.hairline}`, fontWeight: 700, color: t.ink,
  },
  item: {
    display: 'flex', gap: '1rem', padding: '1.1rem 1.25rem', borderBottom: `1px solid ${t.hairline}`,
  },
  thumb: {
    width: 72, height: 90, objectFit: 'cover', borderRadius: t.radiusSm, background: '#e5e7eb', flex: '0 0 auto',
  },
  itemBody: { flex: '1 1 auto', minWidth: 0 },
  itemTop: { display: 'flex', justifyContent: 'space-between', gap: '0.75rem' },
  itemName: { fontWeight: 600, color: t.ink },
  itemSub: { color: t.muted, fontSize: '0.85rem', margin: '0.15rem 0 0.5rem' },
  trash: {
    background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1,
  },
  price: { fontWeight: 700, color: t.ink, whiteSpace: 'nowrap' },
  addonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
    padding: '0.4rem 0.6rem',
    background: t.pageBg,
    borderRadius: t.radiusSm,
    fontSize: '0.85rem',
    color: t.body,
  },
  shipMethod: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    border: `1px solid ${t.border}`,
    borderRadius: t.radiusSm,
    padding: '0.75rem',
    margin: '0 0 1rem',
  },
  shipName: {
    fontWeight: 600, color: t.ink, display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  shipDefault: {
    fontSize: '0.7rem', background: t.mutedSurface, color: t.body, borderRadius: 4, padding: '0.05rem 0.35rem',
  },
  shipDays: { color: t.muted, fontSize: '0.8rem', marginTop: '0.15rem' },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: t.brand,
    cursor: 'pointer',
    padding: 0,
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  posePicker: {
    display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.6rem',
  },
  poseThumbBtn: {
    border: `1px solid ${t.border}`, borderRadius: t.radiusSm, padding: 0, background: t.surface, cursor: 'pointer',
  },
  poseThumb: {
    width: 48, height: 60, objectFit: 'cover', borderRadius: 5, display: 'block',
  },
  summary: { padding: '1.25rem' },
  couponRow: { display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' },
  couponInput: {
    flex: '1 1 auto',
    padding: '0.6rem 0.75rem',
    borderRadius: t.radiusSm,
    border: `1px solid ${t.borderStrong}`,
    fontSize: '0.9rem',
  },
  couponApply: {
    padding: '0.6rem 1rem',
    borderRadius: t.radiusSm,
    border: 'none',
    background: t.mutedSurface,
    color: t.body,
    fontWeight: 700,
    cursor: 'pointer',
  },
  couponApplied: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    borderRadius: t.radiusSm,
    padding: '0.6rem 0.75rem',
    marginBottom: '0.5rem',
  },
  couponErr: { color: t.dangerText, fontSize: '0.8rem', margin: '0.25rem 0 0' },
  shipBox: {
    background: t.brandTint,
    border: `1px solid ${t.brandBorder}`,
    borderRadius: t.radiusSm,
    padding: '0.75rem',
    margin: '1rem 0',
  },
  row: {
    display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', margin: '0.4rem 0', color: t.body,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${t.hairline}`,
    paddingTop: '0.85rem',
    marginTop: '0.5rem',
  },
  totalVal: { fontWeight: 700, fontSize: '1.5rem', color: t.ink },
  checkout: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    boxSizing: 'border-box',
    marginTop: '1rem',
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.85rem',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  secure: {
    textAlign: 'center', color: t.muted, fontSize: '0.8rem', marginTop: '0.75rem',
  },
  empty: {
    background: t.surface,
    borderRadius: t.radius,
    boxShadow: t.shadowSm,
    padding: '3rem',
    textAlign: 'center',
    color: t.muted,
  },
};

const CartView = ({
  cartUrl, cartItemsUrl, posesUrl, couponUrl, couponDeleteUrl, checkoutUrl, csrfToken, onBack, onChange,
}) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // { itemId, poses }
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [couponError, setCouponError] = useState(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken };

  const apply = useCallback((json) => {
    setData(json);
    onChangeRef.current?.({ item_count: json.item_count, total: json.total });
  }, []);

  const load = useCallback(async () => {
    const res = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    apply(await res.json());
  }, [cartUrl, apply]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    setBusy(true);
    try {
      await fetch(`${cartItemsUrl}/${id}`, { method: 'DELETE', headers });
    } finally { setBusy(false); await load(); }
  };

  const openEditor = async (item) => {
    setBusy(true); // show in-flight state while poses load (matches remove/changePhoto)
    try {
      const url = `${posesUrl}?student_image_id=${item.student_image_id}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const json = await res.json();
      setEditing({ itemId: item.id, poses: (json.poses || []).filter((p) => p.ready) });
    } finally { setBusy(false); }
  };

  const changePhoto = async (itemId, poseId) => {
    setBusy(true);
    try {
      const res = await fetch(`${cartItemsUrl}/${itemId}`, {
        method: 'PATCH', headers, body: JSON.stringify({ pose_id: poseId }),
      });
      apply(await res.json());
      setEditing(null);
    } finally { setBusy(false); }
  };

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setCouponError(null);
    try {
      const res = await fetch(couponUrl, { method: 'POST', headers, body: JSON.stringify({ code }) });
      const json = await res.json();
      apply(json);
      if (res.ok) setCode(''); else setCouponError(json.coupon_error || 'That code is not valid.');
    } finally { setBusy(false); }
  };

  const removeCoupon = async () => {
    setBusy(true);
    try {
      const res = await fetch(couponDeleteUrl, { method: 'DELETE', headers });
      apply(await res.json());
    } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div style={s.page}>
        <div style={s.inner}><p style={{ color: t.muted }}>Loading your cart…</p></div>
      </div>
    );
  }

  if (!data.items.length) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <button type="button" style={s.back} onClick={onBack}>← Continue shopping</button>
          <div style={s.empty}>Your cart is empty.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <button type="button" style={s.back} onClick={onBack}>← Continue shopping</button>
        <div style={s.grid}>
          {/* Items */}
          <div style={s.card}>
            <div style={s.cardHead}>{`Cart (${data.item_count} ${data.item_count === 1 ? 'item' : 'items'})`}</div>
            {data.items.map((item) => (
              <div key={item.id} style={s.item}>
                {(item.printess_thumbnail_url || item.pose?.watermark_url) && (
                  <img src={item.printess_thumbnail_url || item.pose.watermark_url} alt="" style={s.thumb} />
                )}
                <div style={s.itemBody}>
                  <div style={s.itemTop}>
                    <div>
                      <div style={s.itemName}>{item.name}</div>
                      <div style={s.itemSub}>
                        {item.option_name}
                        {itemSummary(item) ? ` · ${itemSummary(item)}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={s.trash}
                      disabled={busy}
                      aria-label="Remove"
                      onClick={() => remove(item.id)}
                    >
                      🗑
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" style={s.linkBtn} disabled={busy} onClick={() => openEditor(item)}>
                      Change photo
                    </button>
                    <span style={s.price}>{money(item.price)}</span>
                  </div>
                  {(item.addons || []).map((addon) => (
                    <div key={addon.name} style={s.addonRow}>
                      <span>{`＋ ${addon.name}`}</span>
                      <span>{money(addon.price)}</span>
                    </div>
                  ))}
                  {editing?.itemId === item.id && (
                    <div style={s.posePicker}>
                      {editing.poses.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          style={s.poseThumbBtn}
                          disabled={busy}
                          onClick={() => changePhoto(item.id, p.id)}
                        >
                          <img src={p.watermark_url} alt={`Pose ${p.number}`} style={s.poseThumb} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div style={s.card}>
            <div style={s.cardHead}>Order summary</div>
            <div style={s.summary}>
              {data.coupon ? (
                <div style={s.couponApplied}>
                  <span style={{ color: '#065f46', fontWeight: 600 }}>{`🏷 ${data.coupon} applied`}</span>
                  <button
                    type="button"
                    style={{ ...s.linkBtn, color: '#065f46' }}
                    disabled={busy}
                    onClick={removeCoupon}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div style={s.couponRow}>
                    <input
                      style={s.couponInput}
                      value={code}
                      placeholder="Discount code"
                      onChange={(e) => { setCode(e.target.value); setCouponError(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                    />
                    <button
                      type="button"
                      style={busy ? { ...s.couponApply, opacity: 0.6, cursor: 'not-allowed' } : s.couponApply}
                      disabled={busy}
                      onClick={applyCoupon}
                    >
                      {busy ? 'Applying…' : 'Apply'}
                    </button>
                  </div>
                  {couponError && <p style={s.couponErr}>{couponError}</p>}
                </>
              )}

              {data.shipping === 0 && data.subtotal > 0 && (
                <div style={{
                  ...s.shipBox,
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  color: '#065f46',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
                >
                  ✓ Your order ships free!
                </div>
              )}

              {/* Shipping method (Figma): the single default method + its cost. */}
              <div style={s.shipMethod}>
                <div>
                  <div style={s.shipName}>
                    USPS Standard
                    <span style={s.shipDefault}>Default</span>
                  </div>
                  <div style={s.shipDays}>5–7 business days</div>
                </div>
                <span style={s.price}>{data.shipping === 0 ? 'FREE' : money(data.shipping)}</span>
              </div>

              <div style={s.row}>
                <span>Subtotal</span>
                <span>{money(data.subtotal)}</span>
              </div>
              {data.discount > 0 && (
                <div style={{ ...s.row, color: t.success }}>
                  <span>Discount</span>
                  <span>{`−${money(data.discount)}`}</span>
                </div>
              )}
              {data.shipping > 0 && (
              <div style={s.row}>
                <span>Shipping</span>
                <span>{money(data.shipping)}</span>
              </div>
              )}
              {data.tax > 0 && (
              <div style={s.row}>
                <span>Estimated tax</span>
                <span>{money(data.tax)}</span>
              </div>
              )}
              <div style={s.totalRow}>
                <span style={{ fontWeight: 700, color: t.ink }}>Total</span>
                <span style={s.totalVal}>{money(data.total)}</span>
              </div>

              <a href={checkoutUrl} style={s.checkout}>Checkout</a>
              <p style={s.secure}>🔒 Secure checkout — card payments processed by Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CartView.propTypes = {
  cartUrl: PropTypes.string.isRequired,
  cartItemsUrl: PropTypes.string.isRequired,
  posesUrl: PropTypes.string.isRequired,
  couponUrl: PropTypes.string.isRequired,
  couponDeleteUrl: PropTypes.string.isRequired,
  checkoutUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onChange: PropTypes.func,
};

CartView.defaultProps = { onChange: null };

export default CartView;
